import { useState, useRef, useEffect, useCallback } from "react";
// import { useGetTitle } from "../../../hooks/useGetTitle";
import { useParams } from "react-router-dom";
// import { toast } from "react-toastify";

import { 
  Box, Button, Breadcrumbs,
  Collapse,
  Divider,
  FormControlLabel, FormControl, 
  IconButton, InputAdornment,
  MenuItem, 
  OutlinedInput,
  Switch, Select,
  Typography, TextField,
  useTheme,
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import DescriptionIcon from "@mui/icons-material/Description"; // MUI 문서 아이콘
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import dayjs from "dayjs";

import { 
  useGetTableData, useGetTableDataInQueryString, 
  usePutTableData,
  useDeleteTableData 
} from "../../../components/Table/hooks";

import { 
  assessmentStatusColumns, assessmentStatusColumns_admin 
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { 
  BreadcrumbsTable,
  ConfirmDialog, ConfirmDialogSingle,
  SnackbarAlert, 
  formatDateWithDayjs, formatDateToDayjs, getTodayByDayjs,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

//////////////////////////////////
// 위험성평가 현황 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "평가관리";
const CHILD_TITLE = "위험성평가 현황";
export function AssessmentStatus() {
  const { group_code, project_code } = useParams();
  const theme = useTheme();

  const FILTER_STATUS = Object.freeze({ // 필터 상태
    ALL: "all",
    REGISTERED: "registered",
    DELETED: "deleted",
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다. (평가연도, 평가월, 설비명)
  const [searchStatus, setSearchStatus] = useState({});
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      year: searchStatus.year,
      month: searchStatus.month,
      machine_id: searchStatus.machine_id,
      filter: searchStatus.filter,
    },
  }

  // 위험성 수준 판단 기준 가능성(빈도) 데이터 조회 요청
  const getFrequencyData = useGetTableData(
    "request",
    "/getSafeCalFrequency",
    group_code,
    project_code
  );
  // 위험성 수준 판단 기준 중대성(강도) 데이터 조회 요청
  const getImportancyData = useGetTableData(
    "request",
    "/getSafeCalImportancy",
    group_code,
    project_code
  );
  // 위험성 허용 기준 데이터 조회 요청
  const getStandardData = useGetTableData(
    "request",
    "/getSafeCalStandard",
    group_code,
    project_code
  );

  // 위험요인등록 현황 조회 요청
  const getRegisterStatusData = useGetTableData(
    "request",
    "/getRegisterStatus", 
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getRiskAssessmentStatus",
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putRiskAssessment",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteRiskAssessment",
    group_code,
    project_code
  );

  // 필터링 목록 조회 요청 (평가년도별, 평가년도의 최신 평가 월, register_cause_id을 가져옵니다.)
  const getFilterListData = useGetTableData(
    "request",
    "/getRiskAssessmentStatusFilterList",
    group_code,
    project_code,
  );

  // 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if(!getFilterListData.data || getFilterListData.data.length === 0 ) {
      return;
    }
    if(!getRegisterStatusData.data || getRegisterStatusData.data.length === 0) {
      return;
    }
    
    // 최신 평가연도 데이터 가져오기
    let latestYearData = null;
    for(let i = getFilterListData.data.length - 1; i >= 0; i--) {
      if(getFilterListData.data[i].eval_date_year &&
        getFilterListData.data[i].latest_month &&
        getFilterListData.data[i].register_cause_id) {
        latestYearData = getFilterListData.data[i];
        break;
      }
    }
    if(!latestYearData) {
      return; // 최신 평가연도 데이터가 없으면 처리 중단
    }

    const data = 
    {
      year: latestYearData.eval_date_year,
      month: Number(latestYearData.latest_month),
      machine_id: FILTER_STATUS.ALL, // 모든 설비를 조회하기 위해 'all'로 설정합니다.
      filter: FILTER_STATUS.ALL, 
    };

    setSearchStatus(data);
  }, [getFilterListData.data, getRegisterStatusData.data]);

  // 임시 데이터 상태 관리 (삭제된 항목은 배경색 변경)
  const [tempData, setTempData] = useState([]);
  const handleSetTempData = (data) => setTempData(data);
  useEffect(() => {
    // 데이터 가공
    const data = getData.data.map((item, index) => {
      const isDeleted = item.register_cause_status === 1 
        || item.process_status === 1
        || item.register_asset_status === 1;
      return {
        ...item,
        __index: index,
        __eval_date: formatDateToDayjs(item.eval_date), // 편집용 평가일자
        __approve_date: formatDateToDayjs(item.approve_date), // 편집용 승인일자
        __bgColor: isDeleted
          ? {
            backgroundColor: "#ffebeb", // 연한 빨간색 배경
            backgroundImage:
              "repeating-linear-gradient(45deg, #ffcccc 0, #ffcccc 2px, transparent 2px, transparent 6px)",
            backgroundSize: "8px 8px",
          }
          : theme.palette.background.paper,
      };
    });

    setTempData(data);
  },[getData.data]);

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
    isOpen: false,
    title: '',
    message: '',
  });
  const openMessageAlarm = (title='', message='',) => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeMessageAlarm = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: '',
      message: '',
    });
  };

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 평가연도 목록 (중복 제거를 위한 작업)
  const yearList = Array.from(
    new Set(getFilterListData.data.map(item => item.eval_date_year))
  );
  //console.log(yearList);
  // 설비명 목록 (중복 제거를 위한 작업)
  const machineIdList = Array.from(
    new Map(
      getRegisterStatusData.data.map((item) => [
        item.machine_id, 
        { 
          machine_id: item.machine_id, 
          machine_name: item.machine_name 
        }
      ])
    ).values()
  );
  machineIdList.unshift({ machine_id: FILTER_STATUS.ALL, machine_name: '전체' }); // 전체 추가
  //console.log(machineIdList);

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const filters = [
    { label: "전체", value: FILTER_STATUS.ALL },
    { label: "유지 항목", value: FILTER_STATUS.REGISTERED },
    { label: "삭제 항목", value: FILTER_STATUS.DELETED },
  ];
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>

      {/* 왼쪽 영역 */}
      <Box display={"flex"} alignItems="center" gap={4} marginLeft={'16px'}>
        {/* 평가연도 카테고리 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", }}>
            <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 15, color: theme.text }}>
              평가연도
            </Typography>
            <Select
              value={searchStatus.year || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  year: e.target.value,
                });
              }}
              sx={{
                width: "100px",
                fontWeight: FONT_PROPS.SEMIBOLD,
                color: theme.text,
                fontSize: 15,
              }}
            >
              {/* 필터 목록 */}
              {yearList.reverse().map((year, index) => (
                <MenuItem
                  key={index}
                  value={year}
                  sx={{
                    fontWeight: FONT_PROPS.SEMIBOLD,
                    color: theme.text,
                    fontSize: 15,
                  }}
                >
                  {year}년
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 평가월 카테고리 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", }}>
            <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 15, color: theme.text, }}>
              평가월
            </Typography>
            <Select
              value={searchStatus.month || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  month: e.target.value,
                });
              }}
              sx={{ width: "80px", fontWeight: FONT_PROPS.SEMIBOLD, color: theme.text, fontSize: 15 }}
            >
              {/* 필터 목록 */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reverse().map((month, index) => (
                <MenuItem key={index} value={month} sx={{ fontWeight: FONT_PROPS.SEMIBOLD, color: theme.text, fontSize: 15 }}>
                  {month}월
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 설비명 카테고리 */}
        <FormControl size="small">
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", }}>
            <Typography sx={{ fontWeight: FONT_PROPS.SEMIBOLD, fontSize: 15, color: theme.text }}>
              설비명
            </Typography>
            <Select
              value={searchStatus.machine_id || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  machine_id: e.target.value,
                });
              }}
              sx={{ minWidth: "120px", fontWeight: FONT_PROPS.SEMIBOLD, color: theme.text, fontSize: 15}}
            >
              {/* 필터 목록 */}
              {machineIdList.map((data, index) => (
                <MenuItem key={index} value={data.machine_id} sx={{ fontWeight: FONT_PROPS.SEMIBOLD, color: theme.text, fontSize: 15 }}>
                  {data.machine_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
      </Box>

      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setGlobalFilter('');
                          table.setGlobalFilter('');
                          setShowSearch(false); // 닫기
                        }}
                      >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>

        {/* 필터 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {filters.map((filter) => {
            const isActive = searchStatus.filter === filter.value;
            return (
              <Box
                key={filter.value}
                onClick={() => setSearchStatus({ ...searchStatus, filter: filter.value })}
                sx={{
                  display: "flex",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "1.375rem",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.2s ease",
                  backgroundColor: isActive
                    ? 'rgb(var(--mui-palette-primary-mainChannel) / 0.25)'
                    : "transparent",
                  color: isActive
                    ? '#7367F0'
                    : theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary
                      : theme.palette.action.hover,
                    color: '#7367F0'
                  },
                  "&:active": {
                    transform: "scale(0.97)",
                  },
                }}
              >
                <Typography variant="body2" component="div" color="inherit"
                  sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14, px: 3, py: 2, }}>
                  {filter.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* 삭제버튼 */}
        {showAdminMode && 
          <Buttons.Delete 
            onClick={openDeleteModal} // 행 삭제 로직 실행
            disabled={table.getSelectedRowModel().rows.length === 0}
            sx={{ padding: '12px 16px',}}
          >
            데이터 삭제
          </Buttons.Delete>
        }
        {/* 관리자 모드 */}
        <FormControl component="fieldset">
          <FormControlLabel
            value="top"
            control={
              <Switch color="primary" 
                checked={showAdminMode}
                onChange={(event) => {
                  setShowAdminMode(event.target.checked);
                  clearSelection();
                }}
              />
            }
            label="관리자모드"
            labelPlacement="end" // bottom,end,start,top
            sx={{marginLeft : '4px'}}
          />
        </FormControl>
      </Box>
    </Box>
  );

  // 행 편집 작업 (가능성빈도, 중대성강도)
  const changeRowData = ({ values, row, table }) => {
    //console.log("✅ 편집 호출 완료!", values);
    //console.log("✅ 편집 호출 완료!", row.original);

    // 편집된 데이터 변경
    const findData = tempData.find(item => {
      return item.__index === row.original.__index;
    });
    Object.keys(values).forEach((key) => {
      if (findData[key] !== values[key]) {
        findData[key] = values[key];
      }
    });

    // 데이터 변환
    const frequencyId = getFrequencyId(findData.frequency_level);
    if(frequencyId) {
      findData.frequency_id = frequencyId;
    }
    const importancyId = getImportancyId(findData.importancy_level);
    if(importancyId) {
      findData.importancy_id = importancyId;
    }
    const calculateLevel = calculateRiskLevel(findData.frequency_level, findData.importancy_level);
    const standardId = getStandardId(calculateLevel);
    if(standardId) {
      findData.standard_id = standardId;
    }

    // TempData 변경
    const data = tempData.map(item => {
      return item.__index === row.original.__index ? findData : item;
    });
    handleSetTempData(data);
    
    //table.setEditingRow(null); // 에디터 모드 종료
  };
  // 위험성 평가 현황 테이블의 전체 행 데이터 편집 완료 액션
  const completeEditAllConfirm = showAdminMode ? (
    <Buttons.Custom
      startIcon={<CheckIcon />}
      onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        const findDatas = tempData.filter((item) => {
          // 승인 완료 이후 항목만 수정 가능
          // const isWaiting = item?.eval_status >= 20 ? true : false;
          // 평가일자 및 승인일자가 유효한지 확인
          const isValidDate = dayjs(item.__eval_date).isValid() && dayjs(item.__approve_date).isValid();
          return isValidDate;
          // return isWaiting && isValidDate;
        });
        if(findDatas.length <= 0) {
          openMessageAlarm(
            '안내',
            '전체 수정할 항목이 없습니다.'
          );
          return;
        }

        updateEditDataByAdmin(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 수정
    </Buttons.Custom> 
  ) : null;

  // 위험성 평가 현황 테이블의 개별 행 데이터 편집 완료 액션
  const completeEditConfirm = (rowData) => {
    return(
      <Buttons.Custom
        //startIcon={<CheckIcon />}
        // disabled={rowData?.eval_status < 20} // 승인 이전에는 수정 불가
        onClick={(event) =>{
          event.stopPropagation(); // 이벤트 전파 중단
          if(checkDataByAdmin(rowData)) {
            openMessageAlarm(
              '안내',
              '평가일자 및 승인일자를 설정해주세요.'
            );
            return;
          }
          updateEditDataByAdmin(rowData);
        }}
        sx={{ padding: '8px 12px' }}
      >
        수정
      </Buttons.Custom>
    );
  };

  // DB 업데이트 (수정 데이터) - 관리자 모드
  const updateEditDataByAdmin = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 
      const updateData = [];
      for(const item of tempRowDatas) {
        // 평가일자가 변경되었는지 확인 후 반영
        const __evalDate = formatDateWithDayjs(item.__eval_date);
        const evalDate = formatDateWithDayjs(item.eval_date);
        const isSameEval = dayjs(__evalDate).isSame(dayjs(evalDate), 'day');

        const __approve_date = formatDateWithDayjs(item.__approve_date);
        const approveDate = formatDateWithDayjs(item.approve_date);
        const isSameApprove = dayjs(__approve_date).isSame(dayjs(approveDate), 'day');

        let updateDate = approveDate;
        if(!isSameEval) { updateDate = __evalDate; } // 평가일자가 변경된 경우 우선 반영
        if(!isSameApprove) { updateDate = __approve_date; } // 승인일자가 변경된 경우 최우선 반영

        const data = {
          eval_id: item.eval_id,
          frequency_id: item.frequency_id,
          importancy_id: item.importancy_id,
          standard_id: item.standard_id,
          eval_status: 20, // 평가 상태: 20(승인 완료)
          eval_date: isSameEval ? evalDate : __evalDate,
          approve_date: isSameApprove ? approveDate : __approve_date,
          update_date: updateDate,
        };
        updateData.push(data);
      }
      
      // 데이터베이스 업데이트
      await putData.mutateAsync({ updateData });
      
      openDataAlert(message.addSuccess);
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }

  // 위험성 평가 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });

  // DB 업데이트 (삭제 데이터)
  const deleteEditData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.eval_id);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'eval_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

    ////////////////////////
  // Helper Functions
  ////////////////////////

  // 가능성(빈도) 레벨에 따른 ID 반환
  const getFrequencyId = (freqLevel) => {
    if(freqLevel === null) {
      return freqLevel;
    }
          
    const data = getFrequencyData.data.find( item => {
      return freqLevel === item.frequency_level;
    });
    return data.frequency_id;
  }

  // 중대성(강도) 레벨에 따른 ID 반환
  const getImportancyId = (impLevel) => {
    if(impLevel === null) {
      return impLevel;
    }

    const data = getImportancyData.data.find( item => {
      return impLevel === item.importancy_level;
    });
    return data.importancy_id;
  }

  // 위험도 계산 함수
  const calculateRiskLevel = (freqLevel, impLevel) => {
    const riskValue = freqLevel * impLevel;
    return riskValue;
  };

  // 위험성 허용 기준 ID 반환
  const getStandardId = (riskLevel) => {
    if(riskLevel <= 0) {
      return null;
    } 

    const data = getStandardData.data.find( item => {
      return riskLevel <= item.risk_level_max;
    });
    return data.standard_id;
  };

  // 데이터 빈칸 체크 - 관리자 모드
  const checkDataByAdmin = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    for(const item of tempData) {
      const evalDate = dayjs(item.__eval_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      const approveDate = dayjs(item.__approve_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      if(!evalDate || !approveDate) {
        return true;
      }
    }
    return false;
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {/* 위험성 평가 현황 테이블 */}
        <NewTableTemplate 
          ref={tableRef}
          columns={showAdminMode ? assessmentStatusColumns_admin : assessmentStatusColumns}
          data={tempData || []}
          topToolbarContent={customTableTopToolbar}
          renderRowActionHeader={completeEditAllConfirm}
          renderRowActionContent={completeEditConfirm}
          renderRowActionCustomOptions={changeRowData}
          editDisplayMode={"row"}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={showAdminMode}
          enabledSelectionAllRow={showAdminMode}
          enableEditing={showAdminMode}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }} 
        />
        {/* 삭제 메시지 */}
        <ConfirmDialog  
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteEditData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle  
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeMessageAlarm}
        />
      </TableContainer>
    );
  }

  // AssessmentStatus 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}
